import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ClipboardList, Gauge, Handshake,
  Sun, Building2, Sprout, Factory, Route, HeartPulse, Cpu, Upload,
} from "lucide-react";
import { PriceTable } from "../components/landing/PriceTable";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { JourneyDiagram } from "../components/landing/JourneyDiagram";

const JOURNEY = [
  { icon: ClipboardList, title: "Get your report & tell us about your project", body: "Get your Project Readiness Report, then complete your project profile so we understand your project, where you are, and where you are going." },
  { icon: Upload, title: "Upload your documents", body: "Upload your project and financial documents — feasibility studies, financial model, permits and more. Do not have everything yet? Our team can help you build what you need." },
  { icon: Cpu, title: "We score your project", body: "We review your documents and score your project the way real financiers do — so you know exactly where you stand." },
  { icon: Gauge, title: "Your Project Readiness Score & Report", body: "See your Project Readiness Score and get your Project Readiness Report — which financiers fit, how much you could raise, and exactly what to fix." },
  { icon: Handshake, title: "Clinq goes to work", body: "If your score is strong, Clinq connects you directly to the right financiers in our network. If not, your Relationship Manager walks with you until you are there." },
];

const PROJECT_TYPES = [
  { icon: Sun, title: "Renewable energy", body: "Solar parks, mini-grids, storage and clean-energy rollouts." },
  { icon: Building2, title: "Real estate", body: "Residential, commercial and mixed-use developments." },
  { icon: Sprout, title: "Agriculture", body: "Processing plants, irrigation schemes and out-grower projects." },
  { icon: Route, title: "Infrastructure", body: "Transport, water, logistics hubs and industrial parks." },
  { icon: Factory, title: "Manufacturing", body: "New plants, production lines and capacity expansions." },
  { icon: HeartPulse, title: "Healthcare & social", body: "Hospitals, schools and impact-driven facilities." },
];

const TIERS = [
  { band: "All projects", price: "KES 9,500", note: "One flat price, regardless of stage or raise size", popular: true },
];

const TIER_FEATURES = [
  { label: "Full Project Readiness Report (web + PDF)", href: "/sample-report", hrefLabel: "View sample" },
  { label: "3 re-runs included (4 credits total)",
    info: "Unlocking your Project Readiness Report gives you 4 analysis credits. The first is used when we generate your report; the remaining 3 let you update your documents and re-run your analysis to see your Project Readiness Score improve — no extra charge." },
  "Bankability gaps flagged, with fixes",
  "Matched project financiers for your mandate fit",
  "Improvement roadmap, step by step",
];

export default function ForProjects() {
  const nav = useNavigate();
  return (
    <div data-testid="for-projects-page" className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />

      {/* Header */}
      <header className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
          <Link to="/" data-testid="for-projects-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-[#ECC764]">
            Clinq for Projects · Unlock Your Capital Potential
          </p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Make your project <span className="text-emerald-300">bankable.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/75 sm:text-lg">
            From solar parks to real estate developments — Clinq scores your project the way
            financiers assess it, then connects you to the capital built to fund it.
          </p>
          <button
            data-testid="for-projects-header-cta"
            onClick={() => nav("/get-started-project")}
            className="group mt-8 inline-flex items-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-[#066667]"
          >
            Start free — score my project
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </header>

      {/* How Clinq Works — project journey */}
      <section data-testid="for-projects-journey" className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">How Clinq Works</p>
        <h2 className="mt-3 max-w-2xl text-2xl font-extrabold tracking-tight sm:text-3xl">
          Your project's journey from first login to funder-ready — in five clear steps.
        </h2>
        <JourneyDiagram steps={JOURNEY} testidPrefix="for-projects-journey" />
      </section>

      {/* Project types */}
      <section data-testid="for-projects-types" className="bg-[#F4F8F8]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Built for real-economy projects</p>
          <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
            If it can be built, it can be scored.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PROJECT_TYPES.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="rounded-2xl border border-neutral-200 bg-white p-6 transition-colors hover:border-[#0A8B85]/40">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0A8B85]/10 text-[#0A8B85]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-base font-bold">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="for-projects-pricing" className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#ECC764]">Pricing</p>
          <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
            Your quiz score is free. Your full report is one flat price.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            KES 9,500 for every project, whatever the stage or raise size — no subscription, no surprises.
            Expert review and funder connection are included once your score reaches 55+.
          </p>
          <PriceTable basisLabel="Project stage" rows={TIERS} features={TIER_FEATURES} testidPrefix="for-projects-pricing" />
          <div className="mt-12 flex flex-wrap items-center gap-5">
            <button
              data-testid="for-projects-pricing-cta"
              onClick={() => nav("/get-started-project")}
              className="group inline-flex items-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-[#066667]"
            >
              Score my project free
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="text-sm text-white/50">Secure payments via Paystack · one payment unlocks your full 5-step journey</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
